import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '@/utils/cn';
import {
  countryName,
  countryOfInternational,
  flagEmoji,
  formatNational,
  isMainCountry,
  joinPhone,
  nationalExample,
  phoneCountries,
  splitPhone,
} from '@/utils/phone';
import { getCountryCallingCode } from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/mobile/examples';

import { Field } from './Field';
import { Icon } from './Icon';

/** The frame both halves share, so they read as one field. */
const FRAME =
  'border-line bg-surface rounded-input flex w-full min-w-0 items-stretch border shadow-xs transition ' +
  'hover:border-brand-300 focus-within:border-brand-500 focus-within:ring-brand-100 focus-within:ring-2';

const INVALID =
  'border-danger-500 hover:border-danger-500 focus-within:border-danger-500 focus-within:ring-danger-100';

function matches(country, query) {
  const q = query.trim().toLowerCase().replace(/^\+/, '');
  if (!q) return true;
  return (
    country.name.toLowerCase().includes(q) ||
    country.code.toLowerCase() === q ||
    country.dialCode.startsWith(q)
  );
}

/**
 * A phone number: a country picker ("🇮🇳 +91") and the number box, as one field.
 *
 * Controlled with a single string, the way a form holds it: `value` is the
 * stored number (E.164, or an older free-text one) and `onChange` receives
 * "+<dial code><digits>" for whatever is typed, which `phoneSchema` validates
 * and turns into E.164. India is preselected when there is no number. Pasting
 * "+1 202 555 0143" into the box switches the country to match.
 */
export const PhoneInput = forwardRef(function PhoneInput(
  {
    label,
    error,
    hint,
    required,
    id,
    name,
    value,
    onChange,
    onBlur,
    disabled,
    wrapperClassName,
    className,
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-countries`;

  const [parts, setParts] = useState(() => splitPhone(value));
  const emitted = useRef(value ?? '');

  // A value set from outside (a form reset, a refetch) replaces what is shown.
  useEffect(() => {
    const next = value ?? '';
    if (next !== emitted.current) {
      emitted.current = next;
      setParts(splitPhone(next));
    }
  }, [value]);

  function emit(country, national) {
    setParts({ country, national });
    const next = joinPhone(country, national);
    emitted.current = next;
    onChange?.(next);
  }

  function changeNational(text) {
    // "+1 202 555 0143" typed or pasted into the box: take its country.
    const named = countryOfInternational(text);
    if (named) {
      const split = splitPhone(text);
      if (split.country === named && !split.national.startsWith('+')) {
        emit(named, split.national);
        return;
      }
    }
    emit(parts.country, text);
  }

  const numberRef = useRef(null);
  function setNumberRef(node) {
    numberRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }

  const placeholder = useMemo(() => nationalExample(parts.country, examples), [parts.country]);

  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      hint={hint}
      required={required}
      className={wrapperClassName}
    >
      <div className={cn(FRAME, error && INVALID, disabled && 'opacity-60', className)}>
        <CountryPicker
          country={parts.country}
          listId={listId}
          disabled={disabled}
          onPick={(country) => {
            emit(country, parts.national);
            numberRef.current?.focus();
          }}
        />
        <input
          ref={setNumberRef}
          id={inputId}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={placeholder}
          value={parts.national}
          disabled={disabled}
          required={required}
          aria-invalid={Boolean(error) || undefined}
          onChange={(event) => changeNational(event.target.value)}
          onBlur={(event) => {
            // Tidy the digits once the student moves on ("98765 43210").
            const tidy = formatNational(parts.country, parts.national);
            if (tidy !== parts.national) setParts({ ...parts, national: tidy });
            onBlur?.(event);
          }}
          className="text-ink placeholder:text-ink-muted/70 min-w-0 flex-1 rounded-r-[var(--radius-input)] bg-transparent px-2.5 py-1.5 text-sm focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
    </Field>
  );
});

/** The country button and its searchable list (an ARIA combobox). */
function CountryPicker({ country, listId, disabled, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const buttonRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);

  const all = phoneCountries();
  const shown = useMemo(() => {
    const found = all.filter((item) => matches(item, query));
    // "+44": the United Kingdom before Guernsey, Jersey and the Isle of Man.
    const dial = query.trim().replace(/^\+/, '');
    if (!/^\d+$/.test(dial)) return found;
    const rank = (item) => (item.dialCode === dial ? 0 : 2) - (isMainCountry(item.code) ? 1 : 0);
    return [...found].sort((a, b) => rank(a) - rank(b));
  }, [all, query]);
  const dialCode = getCountryCallingCode(country);

  function openList() {
    setQuery('');
    setActive(
      Math.max(
        0,
        all.findIndex((item) => item.code === country),
      ),
    );
    setOpen(true);
  }

  function close({ focusButton = true } = {}) {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }

  function pick(item) {
    setOpen(false);
    onPick(item.code);
  }

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    function outside(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  // Keep the highlighted country in view while arrowing through the list.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  function onSearchKey(event) {
    const last = shown.length - 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(last, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(0, index - 1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(last);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (shown[active]) pick(shown[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      close({ focusButton: false });
    }
  }

  return (
    <div ref={rootRef} className="relative flex shrink-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country: ${countryName(country)} +${dialCode}`}
        title={`${countryName(country)} +${dialCode}`}
        onClick={() => (open ? close() : openList())}
        onKeyDown={(event) => {
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            openList();
          }
        }}
        className="border-line text-ink hover:bg-brand-50 focus-visible:bg-brand-50 flex items-center gap-1 rounded-l-[var(--radius-input)] border-r px-2 text-sm focus:outline-none disabled:cursor-not-allowed"
      >
        <span className="font-flag text-base leading-none" aria-hidden="true">
          {flagEmoji(country)}
        </span>
        <span className="tabular-nums">+{dialCode}</span>
        <Icon name="chevron" size={14} className="text-ink-muted rotate-90" />
      </button>

      {open && (
        <div className="border-line bg-surface rounded-card absolute top-full left-0 z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] overflow-hidden border shadow-lg">
          <div className="border-line border-b p-1.5">
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-label="Search countries"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={shown[active] ? `${listId}-${shown[active].code}` : undefined}
              placeholder="Search country or code"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKey}
              className="border-line text-ink placeholder:text-ink-muted/70 rounded-input focus:border-brand-500 focus:ring-brand-100 w-full border px-2 py-1 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label="Countries"
            className="max-h-60 overflow-y-auto py-1"
          >
            {shown.length === 0 && (
              <li className="text-ink-muted px-3 py-2 text-sm">No country matches</li>
            )}
            {shown.map((item, index) => (
              <li
                key={item.code}
                id={`${listId}-${item.code}`}
                data-index={index}
                role="option"
                aria-selected={item.code === country}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(item)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                  index === active && 'bg-brand-50',
                  item.code === country && 'text-brand-700 font-medium',
                )}
              >
                <span className="font-flag text-base leading-none" aria-hidden="true">
                  {item.flag}
                </span>
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
                <span className="text-ink-muted tabular-nums">+{item.dialCode}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
